import type { SimulatedDebt } from "@dues/apis/simulated-debt/types";
import type {
	PartnerSystems,
	SortingDebtResponse,
} from "@dues/apis/sorting-debt/types";
import {
	type Dispatch,
	type SetStateAction,
	createContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { formatSDResponseCache } from "./helpers";

import useApiSortingDebt from "@dues/apis/sorting-debt/useApiSortingDebt";
import { cacheSortingDebtIsValid } from "@dues/hooks/useDebts/helpers";
import { useUserStore } from "@dues/hooks/useUserStore";
import {
	legacyBase64Decrypt,
	legacyBase64Encrypt,
} from "@libs/utils/helpers/base64";
import { useDeviceSize } from "@libs/utils/hooks/useDeviceSize";
import constants from "@shared/constants";
import { useUser } from "@shared/hooks/useUser";

import type { DebtFilter, FormattedDebt } from "./types";

import * as caixa from "@dues/helpers/caixa";
import { pageNames } from "@shared/routes/pageNames";

import useDebtsFiltering from "./hooks/useDebtsFiltering";
import useDebtsOrdering from "./hooks/useDebtsOrdering";

import { useAuthentication } from "@libs/acp/contexts";
import useAuthenticatedUser from "@libs/acp/hooks/useAuthenticatedUser";

import useApiSimulatedDebt from "@dues/apis/simulated-debt/useApiSimulatedDebt";
import useSimulatedDebt from "@dues/hooks/useSimulatedDebt";
import { NubankService } from "@libs/renegkit";
import useDebtsTracking from "./hooks/useDebtsTracking";

interface DebtContextType {
	debtsList: FormattedDebt[];
	debtsListSemiSkeletons: any;
	skeletonsCards: any;
	isStatusPartial: boolean;
	isLoadingExperienceController: boolean;
	noDebts: boolean;
	loadedDebtsCount: number;
	filteredDebtsCount: number;
	filteredDebts: FormattedDebt[];
	filterList: DebtFilter[];
	notFoundDebtsText?: string;
	clearFilter: () => void;
	clearNotFoundDebtsMsg: () => void;
	selectFilter: (filter: DebtFilter) => void;
	selectedFilter: DebtFilter | null;
	hasFilter: boolean;
	hasDueDate: boolean;
	setHasDueDate: Dispatch<SetStateAction<boolean>>;
	hasDebtsApiError: boolean;
	loadNewDebtsInLastPos: () => void;
	loadNewDebtsInRegularPos: () => void;
	isModalOfferOpen: boolean;
	setIsModalOfferOpen: Dispatch<SetStateAction<boolean>>;
	showDebtsLoader: boolean;
	setShowDebtsLoader: Dispatch<SetStateAction<boolean>>;
	nubankExperience?: NubankService.NubankExperienceType;
	clearNubankExperience: () => void;
	processedPartnersRef: React.MutableRefObject<Set<string>>;
	isLikelyToCloseAgreement: boolean;
	cleanDebtsContext: () => void;
}

// TODO - move all this logic to ./hooks/index.ts
export const DebtsContext = createContext<DebtContextType>({
	debtsList: [],
	debtsListSemiSkeletons: undefined,
	skeletonsCards: undefined,
	hasDebtsApiError: false,
	isStatusPartial: true,
	isLoadingExperienceController: true,
	noDebts: false,
	loadedDebtsCount: 0,
	filteredDebtsCount: 0,
	filteredDebts: [],
	filterList: [],
	clearFilter: () => {},
	selectFilter: (_filter: DebtFilter) => {},
	clearNotFoundDebtsMsg: () => {},
	selectedFilter: null,
	hasFilter: false,
	hasDueDate: false,
	setHasDueDate: () => {},
	loadNewDebtsInLastPos: () => {},
	loadNewDebtsInRegularPos: () => {},
	isModalOfferOpen: false,
	setIsModalOfferOpen: () => {},
	showDebtsLoader: true,
	setShowDebtsLoader: () => {},
	clearNubankExperience: () => {},
	processedPartnersRef: { current: new Set() },
	isLikelyToCloseAgreement: false,
	cleanDebtsContext: () => {},
});

export const DebtsProvider = ({ children }: any) => {
	const { acpLogout } = useAuthenticatedUser();

	const { document: userDocument, customerIdHash } = useUser();

	const { isMobile } = useDeviceSize();

	const { apiSortingDebtTrack } = useApiSortingDebt();

	const { isAuthenticated, customer } = useAuthentication();

	const firstRequestSortingDebt = useRef(true);
	const isPollingActive = useRef(false);

	const [debtsPollingIntervalId, setDebtsPollingIntervalId] =
		useState<any>(null);

	const [showDebtsLoader, setShowDebtsLoader] = useState<boolean>(
		hasSortingDebtsCache() !== "valid-cache",
	);
	const [debtsList, setDebtsList] = useState<FormattedDebt[]>([]);

	const [debtsListSemiSkeletons, setDebtsListSemiSkeletons] =
		useState<SortingDebtResponse>();
	const [skeletonsCards] = useState(() => [...Array(isMobile ? 1 : 4).keys()]);
	const [isStatusPartial, setIsStatusPartial] = useState<boolean>(true);
	const [isLoadingExperienceController, setIsLoadingExperienceController] =
		useState<boolean>(true);
	const [noDebts, setNoDebts] = useState<boolean>(false);
	const [hasDebtsApiError, setHasDebtsApiError] = useState<boolean>(false);
	const sortingDebtsCallTimes = useRef(0);
	const loadedDebtsCount = debtsList.length;
	const [nubankExperience, setNubankExperience] = useState<
		NubankService.NubankExperienceType | undefined
	>(NubankService.getNubankExperience()?.experience);

	const isLikelyToCloseAgreement = useMemo(() => {
		const restrictiveThreshold = 0.5;
		return debtsList.some((d) => d.propensityToAccept > restrictiveThreshold);
	}, [debtsList]);

	const [hasDueDate, setHasDueDate] = useState<boolean>(false);
	const [isModalOfferOpen, setIsModalOfferOpen] = useState<boolean>(false);

	const {
		filteredDebts,
		filteredDebtsCount,
		filterList,
		clearFilter,
		selectFilter,
		selectedFilter,
		hasFilter,
		notFoundDebtsText,
		clearNotFoundDebtsMsg,
	} = useDebtsFiltering({
		fullDebtList: debtsList,
		isStatusPartial,
		nubankExperience,
	});

	const { orderedDebts, loadNewDebtsInLastPos, loadNewDebtsInRegularPos } =
		useDebtsOrdering({ debtsList, nubankExperience });
	const {
		sendEventDebtLocated,
		sendEventDebtSimulated,
		sendOfferLoadEnded,
		sendOfferLoadStarted,
	} = useDebtsTracking();

	const filterSemiSkeletonCards = (
		responseSortingDebts: SortingDebtResponse,
	) => {
		return {
			...responseSortingDebts,
			partnerSystems: responseSortingDebts?.partnerSystems?.filter(
				(partner) => partner?.debts?.length === 0,
			),
		};
	};

	const checkDebtsUpdate = (
		currentResponseSortingDebts: any,
		debtlist: any,
	) => {
		if (!currentResponseSortingDebts || !debtlist) return true;

		const hasMatchingId = (
			elementA: PartnerSystems,
			elementB: PartnerSystems,
		) => {
			return elementA?.id === elementB?.id;
		};

		const hasAllElementsMatching = (
			sourceList: PartnerSystems[],
			targetList: PartnerSystems[],
		) => {
			return sourceList?.every((sourceElement) =>
				targetList?.some((targetElement) =>
					hasMatchingId(sourceElement, targetElement),
				),
			);
		};

		const hasAllDebtsInTheDebtList =
			hasAllElementsMatching(currentResponseSortingDebts, debtlist) &&
			hasAllElementsMatching(debtlist, currentResponseSortingDebts);

		return !hasAllDebtsInTheDebtList;
	};

	const formattingPartnersInDebts = (partnerSystems: PartnerSystems[]) => {
		const debtsWithPartner = partnerSystems?.reduce(
			(formattedDebts, partnerSystem) => {
				const { debts: partnerDebts, ...partnerProps } = partnerSystem;
				const safeDebts = partnerDebts || [];
				const formattedPartnerDebts = safeDebts.map(
					({ source, product, ...debt }) => {
						return {
							partner: partnerProps,
							...debt,
							source: caixa.capitalizeName(source),
							product: caixa.capitalizeName(product),
						};
					},
				);

				return [...formattedDebts, ...formattedPartnerDebts];
			},
			[] as FormattedDebt[],
		);

		return debtsWithPartner;
	};

	const { shouldPreloadData, referralId, cleanSimulatedDebtsCache } =
		useSimulatedDebt();
	const { apiSimulatedDebt } = useApiSimulatedDebt();

	const simulatedDebts = useUserStore((state) => state.simulatedDebts);
	const { addSimulatedDebts } = useUserStore();
	const processedPartnersRef = useRef<Set<string>>(new Set());

	const handlePreloadSimulatedData = async (
		partnerSystems: PartnerSystems[],
	) => {
		const partnerSystemsWithDebts =
			partnerSystems?.filter((partnerSystem) => {
				const partnerId = partnerSystem?.identifier;
				return (
					partnerSystem.debts &&
					partnerSystem.debts.length > 0 &&
					shouldPreloadData(partnerSystem?.partnerIdentifier) &&
					!simulatedDebts[partnerId] &&
					!processedPartnersRef.current.has(partnerId)
				);
			}) || [];

		if (partnerSystemsWithDebts.length > 0) {
			const cacheUpdates: Record<string, SimulatedDebt[]> = {};

			await Promise.all(
				partnerSystemsWithDebts.map(async (partnerSystem) => {
					const partnerId = partnerSystem?.identifier;
					processedPartnersRef.current.add(partnerId);

					const { dividas: newSimulatedDebts } = await apiSimulatedDebt.send({
						msUrl: partnerSystem.msUrl,
						documento: userDocument,
						partnerSystemIdentifier: partnerId,
						referralId,
					});

					cacheUpdates[partnerId] = newSimulatedDebts;
				}),
			);

			if (Object.keys(cacheUpdates).length > 0) {
				addSimulatedDebts({ ...simulatedDebts, ...cacheUpdates });
			}
		}
	};

	async function handleUpdateDebtsList(
		responseSortingDebts: SortingDebtResponse,
	) {
		setIsStatusPartial(responseSortingDebts.status === "PARTIAL");

		const currentDebtsListSemiSkeletons =
			filterSemiSkeletonCards(responseSortingDebts);

		const hasDebtsSkeletonUpdated = checkDebtsUpdate(
			currentDebtsListSemiSkeletons?.partnerSystems,
			debtsListSemiSkeletons?.partnerSystems,
		);

		if (hasDebtsSkeletonUpdated)
			setDebtsListSemiSkeletons(currentDebtsListSemiSkeletons);

		const formattingSortingDebt = formattingPartnersInDebts(
			responseSortingDebts?.partnerSystems,
		);

		handlePreloadSimulatedData(responseSortingDebts?.partnerSystems);

		const isPollingComplete = responseSortingDebts.status === "COMPLETED";
		sendEventDebtSimulated(formattingSortingDebt, isPollingComplete);
		sendEventDebtLocated(responseSortingDebts?.partnerSystems);

		const noDebts = formattingSortingDebt?.length === 0;

		if (isPollingComplete) {
			setNoDebts(noDebts);
		}

		const hasUpdatedDebts = checkDebtsUpdate(formattingSortingDebt, debtsList);
		if (hasUpdatedDebts) {
			setDebtsList(formattingSortingDebt);
		}
	}

	function hasSortingDebtsCache() {
		const localDebts = localStorage.getItem(constants.debts.SORTING_DEBTS_RES);

		if (localDebts !== null) {
			const decryptedDebts = legacyBase64Decrypt(localDebts);
			const sortingDebtResponse = JSON.parse(decryptedDebts);

			const isValidSDCache = cacheSortingDebtIsValid(sortingDebtResponse?.date);
			const cacheStatus = isValidSDCache ? "valid-cache" : "invalid-cache";
			return cacheStatus;
		}

		return "no-cache";
	}

	const handleSortingDebtsCache = () => {
		const sbCacheStatus = hasSortingDebtsCache();

		if (sbCacheStatus === "valid-cache") {
			const sortingDebtsCache = localStorage.getItem(
				constants.debts.SORTING_DEBTS_RES,
			);
			const decrypted = JSON.parse(
				legacyBase64Decrypt(sortingDebtsCache || ""),
			);
			handleUpdateDebtsList(decrypted);
		} else if (sbCacheStatus === "invalid-cache") {
			cleanSimulatedDebtsCache();
			localStorage.removeItem(constants.debts.SORTING_DEBTS_RES);

			runFirstRequestSortingDebt();
			runPollingSortingDebt();
		} else if (sbCacheStatus === "no-cache") {
			runFirstRequestSortingDebt();
			runPollingSortingDebt();
		}
	};

	const handleUnauthorized = async (data: any) => {
		if (!isAuthenticated || data?.error_slug === "unauthorized") {
			const event = new CustomEvent("logout_trail", {
				detail: {
					trigger: "handle_unauthorized",
					isValid: isAuthenticated,
					error: data?.error_slug,
				},
			});
			window.dispatchEvent(event);

			//ACP
			acpLogout();

			isPollingActive.current = false;
			clearInterval(debtsPollingIntervalId);

			return (window.location.href = pageNames.signin.path);
		}
	};

	const setDebtTagDiscountDrop = (receivedDebtData: {
		partnerSystems: any[];
	}) => {
		const updateLocalStorage = (dataToStore: any) => {
			localStorage.setItem(
				constants.debts.DEBTS_CARD_TAG_DISCOUNT_DROP,
				JSON.stringify(dataToStore),
			);
		};

		const existingStoredData = localStorage.getItem(
			constants.debts.DEBTS_CARD_TAG_DISCOUNT_DROP,
		);

		if (!existingStoredData) {
			const initialFormattedData = {
				partnerSystems: receivedDebtData.partnerSystems.map((partner) => ({
					name: partner?.name,
					identifier: partner?.identifier,
					partnerIdentifier: partner?.partnerIdentifier,
					debts: (partner?.debts || []).map((debt: any) => ({
						contract: debt?.contract,
						id: debt?.id,
						partnerId: debt?.partnerId,
						isShowTag: false,
						paymentOptions: (debt?.paymentOptions || []).map(
							(paymentOption: any) => ({
								id: paymentOption?.id,
								agreementValue: paymentOption?.agreementValue,
								numberInstallment: paymentOption?.numberInstallment,
								discountPercentage: paymentOption?.discountPercentage,
								differenceValue: 0,
							}),
						),
					})),
				})),
			};

			updateLocalStorage(initialFormattedData);
			return;
		}

		const parsedStoredData = JSON.parse(existingStoredData);

		receivedDebtData.partnerSystems.forEach((receivedPartnerSystem) => {
			const matchingStoredPartner = parsedStoredData.partnerSystems.find(
				(storedPartnerSystem: any) =>
					storedPartnerSystem.identifier === receivedPartnerSystem.identifier &&
					storedPartnerSystem.partnerIdentifier ===
						receivedPartnerSystem.partnerIdentifier,
			);

			if (!matchingStoredPartner) {
				parsedStoredData.partnerSystems.push({
					name: receivedPartnerSystem?.name,
					identifier: receivedPartnerSystem?.identifier,
					partnerIdentifier: receivedPartnerSystem?.partnerIdentifier,
					debts: (receivedPartnerSystem?.debts || []).map((debt: any) => ({
						contract: debt?.contract,
						id: debt?.id,
						partnerId: debt?.partnerId,
						isShowTag: false,
						paymentOptions: (debt?.paymentOptions || []).map(
							(paymentOption: any) => ({
								id: paymentOption?.id,
								agreementValue: paymentOption?.agreementValue,
								numberInstallment: paymentOption?.numberInstallment,
								discountPercentage: paymentOption?.discountPercentage,
								differenceValue: 0,
							}),
						),
					})),
				});
				return;
			}

			(receivedPartnerSystem.debts || []).forEach((receivedDebt: any) => {
				const matchingStoredDebt = (matchingStoredPartner.debts || []).find(
					(storedDebt: any) =>
						storedDebt.contract === receivedDebt.contract ||
						storedDebt.id === receivedDebt.id,
				);

				if (!matchingStoredDebt) {
					matchingStoredPartner.debts.push({
						contract: receivedDebt?.contract,
						id: receivedDebt?.id,
						partnerId: receivedDebt?.partnerId,
						isShowTag: false,
						paymentOptions: receivedDebt?.paymentOptions.map(
							(paymentOption: any) => ({
								id: paymentOption?.id,
								agreementValue: paymentOption?.agreementValue,
								numberInstallment: paymentOption?.numberInstallment,
								discountPercentage: paymentOption?.discountPercentage,
								differenceValue: 0,
							}),
						),
					});
					return;
				}

				const updatedPaymentOptions = receivedDebt.paymentOptions.map(
					(receivedOption: any, index: number) => {
						const storedOption = matchingStoredDebt.paymentOptions[index];

						if (storedOption) {
							const receivedValue = receivedOption.agreementValue;
							const storedValue = storedOption.agreementValue;

							if (receivedValue > storedValue) {
								return {
									...receivedOption,
									differenceValue: 0,
								};
							}
							if (receivedValue === storedValue) {
								return storedOption;
							}
							const differenceValue =
								Math.round(Math.abs(receivedValue - storedValue) * 100) / 100;
							if (differenceValue >= 1) {
								return {
									...receivedOption,
									differenceValue: differenceValue,
								};
							}
						}

						return {
							...receivedOption,
							differenceValue: 0,
						};
					},
				);

				const shouldShowDiscountDropTag = updatedPaymentOptions.some(
					(option: any) => option.differenceValue > 0,
				);

				matchingStoredDebt.contract = receivedDebt?.contract;
				matchingStoredDebt.id = receivedDebt?.id;
				matchingStoredDebt.partnerId = receivedDebt?.partnerId;
				matchingStoredDebt.paymentOptions = updatedPaymentOptions;
				matchingStoredDebt.isShowTag = shouldShowDiscountDropTag;
			});
		});

		updateLocalStorage(parsedStoredData);
	};

	const startDebtsPollingAndUpdates = () => {
		const pollingLimit = 45;

		const hasValidCache = hasSortingDebtsCache() === "valid-cache";
		if (
			(!isPollingActive.current && !firstRequestSortingDebt.current) ||
			hasValidCache
		)
			return;
		if (sortingDebtsCallTimes.current >= pollingLimit) {
			isPollingActive.current = false;
			setIsStatusPartial(false);

			clearInterval(debtsPollingIntervalId);

			return;
		}

		const sdPayload = {
			customerIdHash: customerIdHash,
			// adding "Document not found" for tracking later
			document: userDocument || customer?.document || "Document not found",
		};

		apiSortingDebtTrack
			.send(sdPayload)
			.then((response: SortingDebtResponse | any) => {
				setDebtTagDiscountDrop(response);
				handleUpdateDebtsList(response);
				sortingDebtsCallTimes.current = sortingDebtsCallTimes.current + 1;

				if (response?.status === "COMPLETED") {
					isPollingActive.current = false;
					sendOfferLoadEnded();
					clearInterval(debtsPollingIntervalId);

					const formattedResponse = formatSDResponseCache(response);

					const responseEncrypt = legacyBase64Encrypt(
						JSON.stringify(formattedResponse),
					);
					localStorage.setItem(
						constants.debts.SORTING_DEBTS_RES,
						responseEncrypt,
					);
				}

				if (hasDebtsApiError) {
					setHasDebtsApiError(false);
				}
			})
			.catch(async (error) => {
				console.warn("[startDebtsPollingAndUpdates]: ", error);
				sortingDebtsCallTimes.current = sortingDebtsCallTimes.current + 1;

				handleUnauthorized(error);

				if (sortingDebtsCallTimes.current >= pollingLimit) {
					setHasDebtsApiError(true);
				}
			});
	};

	const runFirstRequestSortingDebt = () => {
		if (firstRequestSortingDebt.current) {
			startDebtsPollingAndUpdates();
			sendOfferLoadStarted();
			firstRequestSortingDebt.current = false;
		}
	};

	const runPollingSortingDebt = () => {
		if (isPollingActive.current || firstRequestSortingDebt.current) return;

		isPollingActive.current = true;
		let elapsedSeconds = 0;

		// Define interval durations
		const REQUEST_EVERY_1_SECOND = 1000; // Time between requests for the first 6 seconds
		const MAX_DURATION_FOR_1_SECOND = 6000; // Maximum duration for 1-second requests
		const REQUEST_EVERY_2_SECONDS = 2000; // Time between requests after 6 seconds

		// Function to handle debt requests
		const requestDebts = (interval: number) => {
			startDebtsPollingAndUpdates();
			elapsedSeconds += interval;
		};

		// Set the first interval for 1-second requests
		const firstIntervalId = setInterval(() => {
			if (elapsedSeconds < MAX_DURATION_FOR_1_SECOND) {
				requestDebts(REQUEST_EVERY_1_SECOND); // Requests every 1 second
			} else {
				clearInterval(firstIntervalId);
				// Set the second interval for 2-second requests
				const secondIntervalId = setInterval(() => {
					requestDebts(REQUEST_EVERY_2_SECONDS); // Requests every 2 seconds
				}, REQUEST_EVERY_2_SECONDS);
				setDebtsPollingIntervalId(secondIntervalId);
			}
		}, REQUEST_EVERY_1_SECOND);

		setDebtsPollingIntervalId(firstIntervalId);
	};

	const cleanLSDebt = () => {
		localStorage.removeItem(constants.debts.BILLET_REQ);
		localStorage.removeItem(constants.debts.BILLET_POLL);
		localStorage.removeItem(constants.debts.DEBT);
		localStorage.removeItem(constants.debts.PAYMENT_OPTION);
		localStorage.removeItem(constants.debts.PAYMENT_METHOD);
		localStorage.removeItem(constants.debts.PRESELECTED_PAYMENT_OPTION);
		localStorage.removeItem(constants.debts.PROPERTIES.PAYMENT_DATE);
		localStorage.removeItem(constants.debts.HAS_NEGOTIATION_OPTIONS);
	};

	const cleanDebtsContext = () => {
		setDebtsList([]);
		setDebtsListSemiSkeletons(undefined);
		setIsStatusPartial(true);
		setIsLoadingExperienceController(true);
	};

	useEffect(() => {
		const triggerOnlyOnPaths = [
			"minhas-dividas",
			"dividas",
			"monitoramento",
			"carrinho",
		];
		const currentPath = window?.location?.pathname;
		const currentUrlisValidForTrigger = triggerOnlyOnPaths.some((path) =>
			currentPath.includes(path),
		);
		if (!currentPath.includes("acordo-fechado"))
			localStorage.removeItem(constants.debts.CLOSED_AGREEMENT);

		if (isAuthenticated && currentUrlisValidForTrigger) {
			cleanLSDebt();
			handleSortingDebtsCache();

			return () => {
				clearInterval(debtsPollingIntervalId);
				setDebtsPollingIntervalId(null);
			};
		}
	}, [isAuthenticated, window?.location?.pathname]);

	useEffect(() => {
		if (isPollingActive.current === false && isStatusPartial === false) {
			setIsLoadingExperienceController(false);
		}
	}, [isStatusPartial, isPollingActive.current]);

	const clearNubankExperience = () => {
		setNubankExperience(undefined);
		if (selectedFilter) {
			clearFilter();
		}
	};

	return (
		<DebtsContext.Provider
			value={{
				hasDebtsApiError,
				debtsList: orderedDebts,
				debtsListSemiSkeletons,
				skeletonsCards,
				isStatusPartial,
				isLoadingExperienceController,
				noDebts,
				filteredDebts,
				filterList,
				clearFilter,
				selectFilter,
				selectedFilter,
				notFoundDebtsText,
				hasFilter,
				clearNotFoundDebtsMsg,
				hasDueDate,
				setHasDueDate,
				loadNewDebtsInLastPos,
				loadNewDebtsInRegularPos,
				isModalOfferOpen,
				setIsModalOfferOpen,
				showDebtsLoader,
				setShowDebtsLoader,
				loadedDebtsCount,
				filteredDebtsCount,
				nubankExperience,
				clearNubankExperience,
				processedPartnersRef,
				isLikelyToCloseAgreement,
				cleanDebtsContext,
			}}
		>
			{children}
		</DebtsContext.Provider>
	);
};
